<template>
  <Input v-if="useLegacyDesign" ref="inputRef" />
  <AwTextField v-else ref="inputRef" leading-icon="i-ph-map-pin" :placeholder="placeholder" class="w-full" />
</template>

<script setup lang="ts">
import { useGoogleMaps } from "~/composables/useGoogleMaps";

type Emits = { onPlaceChanged: [result: google.maps.places.PlaceResult] };

const emit = defineEmits<Emits>();

type Props = {
  addressValidation?: (addressComponents: google.maps.GeocoderAddressComponent[]) => void;
  /** Renders the legacy input; pass false for the design system text field. */
  useLegacyDesign?: boolean;
  placeholder?: string;
};

const props = withDefaults(defineProps<Props>(), {
  addressValidation: undefined,
  useLegacyDesign: true,
  placeholder: undefined,
});

const { loadPlaces } = useGoogleMaps();
const Places = await loadPlaces();

// const inputRef = useTemplateRef("autocomplete");
const inputRef = ref();
// The legacy Input exposes `input`, AwTextField exposes `inputRef`
const input = computed(() => inputRef.value?.input ?? inputRef.value?.inputRef);

let autocomplete: google.maps.places.Autocomplete;

onMounted(async () => {
  if (!input.value) throw new Error("Input element is missing");

  autocomplete = new Places.Autocomplete(input.value, {
    types: ["establishment", "geocode"],
    // `place_id` is what lets a server re-resolve the address itself; without it here the widget
    // returns none, since specifying `fields` at all restricts the response to exactly this list.
    fields: ["place_id", "formatted_address", "geometry.location", "address_components"],
    componentRestrictions: {
      // Restrict the search to France, Monaco, Belgium, and Switzerland
      country: ["FR", "MC", "BE", "CH", "RO"],
    },
  });

  autocomplete.addListener("place_changed", () => {
    const place = autocomplete.getPlace();

    if (props.addressValidation && place.address_components) {
      props.addressValidation(place.address_components);
    }

    emit("onPlaceChanged", place);
  });
});
</script>

<style>
/* Google appends the suggestion list to body, outside the modal layer that blocks pointer events */
.pac-item {
  pointer-events: auto !important;
  cursor: pointer;
}
</style>
